import * as React from "react";
import * as ReactDOM from "react-dom";
import * as VSS from "VSS/VSS";
import * as SDK_Shim from "VSS/SDK/Shim";
import * as NavigationServices from "VSS/Navigation/Services";
import { getPageContext } from "VSS/Context";
import { WorkItemFormWrapper } from "WorkItemsHub/Scripts/WorkItemFormWrapper";
import { navigateToWorkItemHub } from "WorkItemsHub/Scripts/Utils/WorkItemsXhrNavigationUtils";
import * as WIHNavDataService_NOREQUIRE from "WorkItemsHubNavDataService/IWorkItemsHubNavDataService";
import * as WorkItemsHubNavView_NOREQUIRE from "WorkItemsHub/Scripts/Components/WorkItemsHubNavView";
import { IVssPageContext } from "VSS/Platform/Context";
import { ViewType } from "WorkItemsHub/Scripts/Components/WorkItemsHubNavView";
import { IVssContributionService } from "VSS/Platform/Contribution";

const WIHNavServiceContribution = "ms.vss-work-web.work-items-hub-nav-data-service";

SDK_Shim.VSS.register("workitemform.view", (context: SDK_Shim.InternalContentContextData): IPromise<IDisposable> | IDisposable => {
    // entry point for _workitem/edit/{id}
    const tfsContext = getPageContext();
    const parameters = tfsContext.navigation.routeValues["id"];
    const id = parseInt(parameters, 10);
    const pageContext: IVssPageContext = context.options._pageContext;
    const requestParameters = getCurrentRequestParameters();
    const navigationData = getWorkItemsHubNavigationData(pageContext);
    const hasTriageData = !!(navigationData && navigationData.workItemIds && navigationData.workItemIds.length > 0);

    if (!id) {
        navigateToWorkItemHub();
        return null;
    }

    if (hasTriageData) {
        return getTriageView(context, pageContext, requestParameters, navigationData, id);
    }

    return WorkItemFormWrapper.createForView(pageContext, context.container, id, requestParameters);
});

SDK_Shim.VSS.register("workitemform.create", (context: SDK_Shim.InternalContentContextData): IPromise<IDisposable> | IDisposable => {
    // entry point for _workitem/create/{workitemtype}
    const pageContext = context.options._pageContext;
    const tfsContext = getPageContext();
    const workItemType = tfsContext.navigation.routeValues["parameters"];
    const triageData = getWorkItemsHubNavigationData(pageContext);
    const hasPrevTabData = !!(triageData && triageData.tabId);

    if (workItemType) {
        if (hasPrevTabData) {
            return getCreateView(context, pageContext, triageData, workItemType);
        }
        return WorkItemFormWrapper.createForNew(pageContext, context.container, workItemType, getCurrentRequestParameters());
    }

    navigateToWorkItemHub();
    return null;
});

function getCurrentRequestParameters(): IDictionaryStringTo<string> {
    const historyService = NavigationServices.getHistoryService();
    const fragment = historyService.getCurrentFragment();
    return NavigationServices.HistoryService.deserializeState(fragment);
}

function getTriageView(context: SDK_Shim.InternalContentContextData,
    pageContext: IVssPageContext,
    requestParameters: IDictionaryStringTo<string>,
    navigationData: WIHNavDataService_NOREQUIRE.IWorkItemsHubNavData,
    id: number): IPromise<IDisposable> {

    let startingIndex = (navigationData.workItemIds && id) ? navigationData.workItemIds.indexOf(id) : -1;

    if (startingIndex < 0) {
        // For unknown id, reset triage data
        startingIndex = 0;
        navigationData.workItemIds = [id];
    }

    return VSS.requireModules(["WorkItemsHub/Scripts/Components/WorkItemsHubNavView"])
        .spread((m: typeof WorkItemsHubNavView_NOREQUIRE) => {
            ReactDOM.render(
                <m.WorkItemsHubNavView
                    viewType={ViewType.Triage}
                    tabId={navigationData.tabId}
                    tabName={navigationData.tabName}
                    startingIndex={startingIndex}
                    workItemIds={navigationData.workItemIds}
                    ref={this._onTriageViewRef}
                    onNavigate={(lastTriagedWorkItemId: number) =>
                        setWorkItemsHubNavigationData(pageContext, { ...navigationData, lastTriagedWorkItemId } as WIHNavDataService_NOREQUIRE.IWorkItemsHubNavData)}
                    requestParameters={requestParameters}
                    pageContext={pageContext}
                />,
                context.container);

            return { dispose: () => ReactDOM.unmountComponentAtNode(context.container) } as IDisposable;
        }) as IPromise<IDisposable>;
}

function getCreateView(context: SDK_Shim.InternalContentContextData,
    pageContext: IVssPageContext,
    triageData: WIHNavDataService_NOREQUIRE.IWorkItemsHubNavData,
    workItemType: string): IPromise<IDisposable> {
    return VSS.requireModules(["WorkItemsHub/Scripts/Components/WorkItemsHubNavView"])
        .spread((m: typeof WorkItemsHubNavView_NOREQUIRE) => {
            ReactDOM.render(
                <m.WorkItemsHubNavView
                    viewType={ViewType.Creation}
                    tabId={triageData.tabId}
                    ref={this._onTriageViewRef}
                    pageContext={pageContext}
                    workItemType={workItemType}
                />,
                context.container);

            return { dispose: () => ReactDOM.unmountComponentAtNode(context.container) } as IDisposable;
        }) as IPromise<IDisposable>;
}

function setWorkItemsHubNavigationData(pageContext: IVssPageContext, data: WIHNavDataService_NOREQUIRE.IWorkItemsHubNavData): void {
    // IWorkItemsHubNavDataService would be registered only if the user navigates from WIH.
    // If the user navigated to the work item directly, the service wouldn't be registered, which is a valid scenario.
    // Thus, retrieve the service by using getServiceSync which passes noThrowIfUndefined = true, so that getService doesn't throw.
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const navigationDataService = contributionService.getServiceSync<WIHNavDataService_NOREQUIRE.IWorkItemsHubNavDataService>(WIHNavServiceContribution);
    if (navigationDataService) {
        navigationDataService.setNavigationData(data);
    }
}

function getWorkItemsHubNavigationData(pageContext: IVssPageContext): WIHNavDataService_NOREQUIRE.IWorkItemsHubNavData {
    // IWorkItemsHubNavDataService would be registered only if the user navigates from WIH.
    // If the user navigated to the work item directly, the service wouldn't be registered, which is a valid scenario.
    // Thus, retrieve the service by using getServiceSync which passes noThrowIfUndefined = true, so that getService doesn't throw.
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const navigationDataService = contributionService.getServiceSync<WIHNavDataService_NOREQUIRE.IWorkItemsHubNavDataService>(WIHNavServiceContribution);
    return navigationDataService && navigationDataService.getNavigationData();
}
